import { handleRefreshTokenFlow } from '@edusense/shared-auth-core/utils/refresh';
import { tokenManager } from '@edusense/shared-auth-core/token-manager';
import { ADMIN_OAUTH_CLIENT_ID, getAdminAuthServerUrl } from './adminOAuth';
import { adminAuthConfig } from './adminAuthConfig';

type TokenStorage = {
  getItem: (key: string) => string | null;
  setItem?: (key: string, value: string) => void;
  removeItem?: (key: string) => void;
};

export const ADMIN_AUTH_TOKEN_STORAGE_KEY = adminAuthConfig.storage.tokenKey;
export const ADMIN_REFRESH_TOKEN_STORAGE_KEY = adminAuthConfig.storage.refreshTokenKey;
export const ADMIN_TOKEN_TYPE_STORAGE_KEY = adminAuthConfig.storage.tokenTypeKey;
export const ADMIN_TOKEN_EXPIRES_AT_STORAGE_KEY = adminAuthConfig.storage.tokenExpiresAtKey;

type RefreshAdminAccessTokenOptions = {
  authServerUrl?: string;
  now?: () => number;
  refreshFlow?: typeof handleRefreshTokenFlow;
  publishToken?: (token: string, tokenType: string) => void;
  clearSession?: () => void;
  storage?: TokenStorage | null;
};

let pendingRefreshPromise: Promise<string | null> | null = null;

const getBrowserStorage = (): TokenStorage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const normalizeToken = (token?: string | null) => {
  const trimmedToken = token?.trim();
  return trimmedToken ? trimmedToken : null;
};

export const resolveAdminAccessToken = (
  authStateToken?: string | null,
  storage: TokenStorage | null = getBrowserStorage()
) => normalizeToken(storage?.getItem(ADMIN_AUTH_TOKEN_STORAGE_KEY)) ?? normalizeToken(authStateToken);

export const hasAdminAuthorizationHeader = (headers: Record<string, string>) => {
  const authorization = headers['Authorization']?.trim();
  return authorization ? /^Bearer\s+\S+/.test(authorization) : false;
};

export const refreshAdminAccessToken = ({
  authServerUrl = getAdminAuthServerUrl(),
  now = Date.now,
  refreshFlow = handleRefreshTokenFlow,
  publishToken = (token, type) => tokenManager.setToken(token, type),
  clearSession = () => tokenManager.clearToken(),
  storage = getBrowserStorage(),
}: RefreshAdminAccessTokenOptions = {}) => {
  if (pendingRefreshPromise) {
    return pendingRefreshPromise;
  }

  pendingRefreshPromise = (async () => {
    const refreshToken = normalizeToken(storage?.getItem(ADMIN_REFRESH_TOKEN_STORAGE_KEY));
    if (!refreshToken || !storage?.setItem) {
      return null;
    }

    try {
      // Share the refresh lock with AuthProvider's scheduled refresh and API clients.
      const result = await refreshFlow(refreshToken, authServerUrl,
        storage.getItem(ADMIN_TOKEN_TYPE_STORAGE_KEY) || 'Bearer', ADMIN_OAUTH_CLIENT_ID);

      const nextAccessToken = normalizeToken(result.accessToken);
      if (!result.success || !nextAccessToken) {
        if (result.error === 'invalid_grant'
          && normalizeToken(storage.getItem(ADMIN_REFRESH_TOKEN_STORAGE_KEY)) === refreshToken) {
          for (const key of Object.values(adminAuthConfig.storage)) {
            storage.removeItem?.(key);
          }
          clearSession();
        }
        return null;
      }

      if (result.refreshToken) {
        storage.setItem(ADMIN_REFRESH_TOKEN_STORAGE_KEY, result.refreshToken);
      }
      if (result.expiresIn) {
        storage.setItem(ADMIN_TOKEN_EXPIRES_AT_STORAGE_KEY, String(now() + result.expiresIn * 1000));
      }
      const tokenType = result.tokenType || 'Bearer';
      storage.setItem(ADMIN_AUTH_TOKEN_STORAGE_KEY, nextAccessToken);
      storage.setItem(ADMIN_TOKEN_TYPE_STORAGE_KEY, tokenType);
      publishToken(nextAccessToken, tokenType);

      return nextAccessToken;
    } catch {
      console.warn('[adminAccessToken] Failed to refresh admin access token');
      return null;
    }
  })().finally(() => {
    pendingRefreshPromise = null;
  });

  return pendingRefreshPromise;
};
